import { Outlet, useParams, useNavigate } from 'react-router-dom';
import {
  GitBranch,
  Package,
  Tag,
  Store,
  ShoppingBag,
  ArrowRightLeft,
  SettingsIcon,
  Percent,
  Settings,
  Truck,
  Languages,
  Plug,
  Crown,
  UserCog,
  Repeat,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import UnifiedNavigation, { MainTab } from '@/components/ui/unified-navigation';
import { STORE_ROUTES } from '@/config/router';
import { cn } from "@/lib/utils";
import { useAccountManager } from '@/hooks/use-account-manager';
import { usePublishDevTabStoreName } from '@/hooks/use-dev-tab-store';
import { useSyncI18nRxName } from '@/hooks/use-sync-i18n-branding';

/**
 * Layout component for store detail pages
 * Provides navigation between products, storefront, integrations, and settings
 */
export default function StoreLayout() {
  const { t } = useTranslation(['stores', 'common']);
  const { id, orgSlug } = useParams();
  const navigate = useNavigate();
  const { isAccountManager, isLoading: isLoadingAccountManager } = useAccountManager();
  usePublishDevTabStoreName(id);
  useSyncI18nRxName(id ?? '');

  if (!orgSlug || !id) {
    throw new Error('Org slug and id are required');
  }
  const currentPath = window.location.pathname;
  const getActiveTab = () => {
    if (currentPath.includes('/storefront')) return 'storefront';
    if (currentPath.includes('/funnels')) return 'funnels';
    if (currentPath.includes('/integrations')) return 'integrations';
    if (currentPath.includes('/settings')) return 'settings';
    return 'general';
  };

  const activeTab = getActiveTab();
  // Define main tabs
  const mainTabs: MainTab[] = [
    {
      id: 'general',
      label: t('stores:general'),
      icon: Store,
      path: STORE_ROUTES(orgSlug, id).STORE_GENERAL(),
    },
    {
      id: 'storefront',
      label: t('stores:storefront'),
      icon: ShoppingBag,
      path: STORE_ROUTES(orgSlug, id).STOREFRONT(),
    },
    {
      id: 'integrations',
      label: t('stores:integrations'),
      icon: Plug,
      path: STORE_ROUTES(orgSlug, id).INTEGRATIONS(),
    },
    {
      id: 'settings',
      label: t('stores:storeSettings'),
      icon: Settings,
      path: STORE_ROUTES(orgSlug, id).STORE_EDIT(),
    },
  ];

  const SUBMENU_CONFIG = (orgSlug: string, id: string): Record<string, MainTab[]> => {
    const storefrontTabs = [
      {
        id: 'funnels',
        label: t('stores:submenu.storefrontFunnels'),
        path: STORE_ROUTES(orgSlug, id).FUNNELS(),
        icon: GitBranch,
      },
      {
        id: 'marketplace',
        label: t('stores:submenu.marketplace'),
        path: STORE_ROUTES(orgSlug, id).STOREFRONT_MARKETPLACE(),
        icon: ShoppingBag,
      },
      // {
      //   id: 'direct-links',
      //   label: t('stores:submenu.directLinks'),
      //   path: STORE_ROUTES(orgSlug, id).STOREFRONT_DIRECT_LINKS(),
      //   icon: LinkIcon,
      // }
    ];

    // Only add routes tab for account managers
    if (isAccountManager) {
      storefrontTabs.push({
        id: 'routes',
        label: t('stores:submenu.mountPoints'),
        path: STORE_ROUTES(orgSlug, id).STOREFRONT_MOUNT_POINTS(),
        icon: ArrowRightLeft,
      });
    }

    return {
      general: [
        {
          id: 'products',
          label: t('stores:submenu.products'),
          path: STORE_ROUTES(orgSlug, id).PRODUCTS(),
          icon: Package,
        },
        {
          id: 'offers',
          label: t('stores:submenu.offers'),
          path: STORE_ROUTES(orgSlug, id).OFFERS(),
          icon: Package,
        },
        {
          id: 'discounts',
          label: t('stores:submenu.discounts'),
          path: STORE_ROUTES(orgSlug, id).DISCOUNTS(),
          icon: Tag,
        },
        // {
        //   id: 'upsells',
        //   label: t('stores:submenu.upsells'),
        //   path: STORE_ROUTES(orgSlug, id).UPSELLS(),
        //   icon: ArrowRightLeft,
        // },
        {
          id: 'post-purchase',
          label: t('stores:submenu.postPurchase'),
          path: STORE_ROUTES(orgSlug, id).POST_PURCHASE(),
          icon: Package,
        },
        {
          id: 'taxes',
          label: t('stores:submenu.taxes'),
          path: STORE_ROUTES(orgSlug, id).TAXES(),
          icon: Percent,
        },
        {
          id: 'shippings',
          label: t('stores:submenu.shippings'),
          path: STORE_ROUTES(orgSlug, id).SHIPPINGS(),
          icon: Truck,
        },
      ],
      storefront: storefrontTabs,
      integrations: [],
      settings: [
        {
          id: 'general',
          label: t('stores:submenu.general'),
          path: STORE_ROUTES(orgSlug, id).STORE_EDIT(),
          icon: SettingsIcon,
        },
        {
          id: 'club',
          label: t('stores:submenu.club'),
          path: STORE_ROUTES(orgSlug, id).CLUB_SETTINGS(),
          icon: Crown,
        },
        {
          id: 'customer-portal',
          label: t('stores:submenu.customerPortal'),
          path: STORE_ROUTES(orgSlug, id).CUSTOMER_PORTAL_SETTINGS(),
          icon: UserCog,
        },
        {
          id: 'recurring-discounts',
          label: t('stores:submenu.recurringDiscounts'),
          path: STORE_ROUTES(orgSlug, id).RECURRING_DISCOUNTS_SETTINGS(),
          icon: Repeat,
        },
        {
          id: 'languages',
          label: t('stores:submenu.languages'),
          path: STORE_ROUTES(orgSlug, id).TRANSLATIONS(),
          icon: Languages,
        },
      ],
    };
  };

  const subMenuItems = SUBMENU_CONFIG(orgSlug, id)[activeTab] || [];

  const getActiveSubmenuId = (): string => {
    const currentPath = window.location.pathname;
    const activeTab = getActiveTab();
    const matched = SUBMENU_CONFIG(orgSlug, id)[activeTab].find((item) => {
      if (item.path === '') {
        return !SUBMENU_CONFIG(orgSlug, id)[activeTab].some(
          (otherItem) => otherItem.path !== '' && currentPath.includes(otherItem.path),
        );
      }
      return currentPath.includes(item.path);
    });
    return matched?.id || '';
  };

  const handleTabChange = (tab: MainTab) => {
    return navigate(tab.path);
  };

  const handleSubmenuClick = (item: MainTab) => {
    return navigate(item.path);
  };

  // Check if we're on the funnel editor page
  const isFunnelEditorPage = currentPath.includes('/storefront/funnelv2_');

  // Check if we're on product or variant details/edit pages (hide navigation for these detailed views)
  const isProductDetailsPage =
    /\/products\/product_[^/]+(?:\/(?:edit|variants\/(?:variant_[^/]+|create)))?$/.test(currentPath);

  // Emails is now a top-level per-store section (sidebar), not part of the
  // settings sub-nav. Hide the in-page UnifiedNavigation so no main tab gets
  // incorrectly highlighted.
  const isEmailsPage = /\/emails(\/|$)/.test(currentPath);

  // Tagada Rx lives in its own self-contained space with its own sub-nav
  // (RxNav). The store's main tab bar is irrelevant there and only adds noise,
  // so hide it entirely inside `/rx/*`.
  const isRxPage = /\/rx(\/|$)/.test(currentPath);

  // Hide navigation on these pages
  const shouldHideNavigation =
    isFunnelEditorPage || isProductDetailsPage || isEmailsPage || isRxPage;

  return (
    <div className={cn('flex flex-col', isFunnelEditorPage && 'h-full overflow-hidden')}>
      {/* Unified Navigation - combines main tabs and sub-navigation */}
      {/* Hide navigation on funnel editor page, product/variant details pages, and while loading account manager status to prevent flickering */}
      {!shouldHideNavigation && !isLoadingAccountManager && (
        <div className="flex flex-row">
          <UnifiedNavigation
            mainTabs={mainTabs}
            activeMainTab={activeTab}
            subTabs={subMenuItems}
            activeSubTab={getActiveSubmenuId()}
            onMainTabChange={handleTabChange}
            onSubTabChange={handleSubmenuClick}
          />
        </div>
      )}
      {/* Nested route content */}
      <Outlet />
    </div>
  );
}
